import { Modal, Tag } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  AdvisoryFindingType,
  type AdvisoryFinding,
  type AdvisoryFindingTypeValue,
} from '@/features/advisory-ai/types'

export type FindingDiff = {
  created: Array<Pick<AdvisoryFinding, 'type' | 'heading' | 'description' | 'hidden'>>
  updated: Array<{ before: AdvisoryFinding; after: AdvisoryFinding }>
  deleted: AdvisoryFinding[]
}

type Props = {
  open: boolean
  agendaTitle: string
  diff: FindingDiff
  saving: boolean
  onConfirm: () => void
  onCancel: () => void
}

const TYPE_LABEL: Record<AdvisoryFindingTypeValue, string> = {
  [AdvisoryFindingType.METRICS]: 'Key metric',
  [AdvisoryFindingType.FINDINGS]: 'Finding',
  [AdvisoryFindingType.RECOMMENDATIONS]: 'Recommendation',
}

function typeLabel(type: string) {
  const key = String(type || '').toLowerCase() as AdvisoryFindingTypeValue
  return TYPE_LABEL[key] ?? type
}

type DiffSeg = { kind: 'common' | 'remove' | 'add'; text: string }

function tokenize(s: string): string[] {
  return s.match(/\S+|\s+/g) ?? []
}

function pushSeg(segs: DiffSeg[], kind: DiffSeg['kind'], text: string) {
  const last = segs[segs.length - 1]
  if (last && last.kind === kind) last.text += text
  else segs.push({ kind, text })
}

function diffWords(before: string, after: string): DiffSeg[] {
  const a = tokenize(before)
  const b = tokenize(after)
  const m = a.length
  const n = b.length
  const stride = n + 1
  const dp = new Int32Array((m + 1) * stride)
  const get = (idx: number): number => dp[idx] ?? 0
  for (let i = m - 1; i >= 0; i--) {
    const ai = a[i] as string
    for (let j = n - 1; j >= 0; j--) {
      const bj = b[j] as string
      dp[i * stride + j] =
        ai === bj
          ? get((i + 1) * stride + (j + 1)) + 1
          : Math.max(get((i + 1) * stride + j), get(i * stride + (j + 1)))
    }
  }
  const segs: DiffSeg[] = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    const ai = a[i] as string
    const bj = b[j] as string
    if (ai === bj) {
      pushSeg(segs, 'common', ai)
      i++
      j++
    } else if (get((i + 1) * stride + j) >= get(i * stride + (j + 1))) {
      pushSeg(segs, 'remove', ai)
      i++
    } else {
      pushSeg(segs, 'add', bj)
      j++
    }
  }
  while (i < m) pushSeg(segs, 'remove', a[i++] as string)
  while (j < n) pushSeg(segs, 'add', b[j++] as string)
  return segs
}

function InlineDiff({ before, after }: { before: string; after: string }) {
  const segs = diffWords(before, after)
  return (
    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
      {segs.map((seg, idx) => {
        if (seg.kind === 'common') return <span key={idx}>{seg.text}</span>
        if (seg.kind === 'remove') {
          return (
            <span
              key={idx}
              className="bg-red-100 text-red-800 line-through rounded-sm px-0.5"
            >
              {seg.text}
            </span>
          )
        }
        return (
          <span key={idx} className="feature-soft-surface feature-soft-text rounded-sm px-0.5">
            {seg.text}
          </span>
        )
      })}
    </p>
  )
}

function ChangeBlock({
  label,
  before,
  after,
}: {
  label: string
  before: string
  after: string
}) {
  if (before === after) return null
  return (
    <div className="text-sm">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <InlineDiff before={before} after={after} />
    </div>
  )
}

export function FindingsChangesConfirmModal({
  open,
  agendaTitle,
  diff,
  saving,
  onConfirm,
  onCancel,
}: Props) {
  const totalChanges = diff.created.length + diff.updated.length + diff.deleted.length

  return (
    <Modal
      open={open}
      title={`Confirm changes to "${agendaTitle}"`}
      onOk={onConfirm}
      onCancel={onCancel}
      okText={`Confirm ${totalChanges} change${totalChanges === 1 ? '' : 's'}`}
      cancelText="Back to editing"
      confirmLoading={saving}
      okButtonProps={{ disabled: totalChanges === 0 }}
      width={640}
      maskClosable={!saving}
      closable={!saving}
      destroyOnClose
    >
      <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
        {diff.created.length > 0 ? (
          <section>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
              <PlusOutlined className="text-green-600" />
              Added <span className="text-gray-500 font-normal">({diff.created.length})</span>
            </h4>
            <div className="space-y-2">
              {diff.created.map((c, i) => (
                <div
                  key={i}
                  className="rounded-md border border-green-200 bg-green-50/60 p-3"
                >
                  <Tag color="green" className="!mb-2">
                    {typeLabel(c.type)}
                  </Tag>
                  {c.heading ? (
                    <p className="text-sm font-semibold text-gray-900">{c.heading}</p>
                  ) : null}
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.description}</p>
                  {c.hidden ? (
                    <p className="text-xs text-gray-500 mt-1">Hidden from outputs</p>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {diff.updated.length > 0 ? (
          <section>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
              <EditOutlined className="feature-soft-text" />
              Modified <span className="text-gray-500 font-normal">({diff.updated.length})</span>
            </h4>
            <div className="space-y-3">
              {diff.updated.map(({ before, after }) => (
                <div
                  key={before.advisoryFindingId}
                  className="feature-soft-surface feature-soft-border rounded-md border p-3 space-y-2"
                >
                  <span className="feature-soft-surface feature-soft-border feature-soft-text inline-block text-xs font-medium px-2 py-0.5 mr-1 rounded border">
                    {typeLabel(after.type)}
                  </span>
                  <ChangeBlock
                    label="Heading"
                    before={before.heading || ''}
                    after={after.heading || ''}
                  />
                  <ChangeBlock
                    label="Description"
                    before={before.description}
                    after={after.description}
                  />
                  {String(before.type).toLowerCase() !== String(after.type).toLowerCase() ? (
                    <ChangeBlock
                      label="Type"
                      before={typeLabel(before.type)}
                      after={typeLabel(after.type)}
                    />
                  ) : null}
                  {Boolean(before.hidden) !== Boolean(after.hidden) ? (
                    <ChangeBlock
                      label="Hidden from outputs"
                      before={before.hidden ? 'Yes' : 'No'}
                      after={after.hidden ? 'Yes' : 'No'}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {diff.deleted.length > 0 ? (
          <section>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
              <DeleteOutlined className="text-red-600" />
              Removed <span className="text-gray-500 font-normal">({diff.deleted.length})</span>
            </h4>
            <div className="space-y-2">
              {diff.deleted.map((d) => (
                <div
                  key={d.advisoryFindingId}
                  className="rounded-md border border-red-200 bg-red-50/60 p-3"
                >
                  <Tag color="red" className="!mb-2">
                    {typeLabel(d.type)}
                  </Tag>
                  {d.heading ? (
                    <p className="text-sm font-semibold text-gray-900 line-through">{d.heading}</p>
                  ) : null}
                  <p className="text-sm text-gray-700 line-through whitespace-pre-wrap">
                    {d.description}
                  </p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {totalChanges === 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-4">No changes to save.</p>
        ) : null}
      </div>
    </Modal>
  )
}
